import { Component, computed, effect, Inject, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { CallerService, CallerRole } from '../caller.service';
import { ConnectService } from '../services/amazon-connect.service';
import { MatIconModule } from '@angular/material/icon';
import { Field, PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { Store } from '@ngrx/store';
import { setCallerRole } from '../store/selection/selection.actions';

// caller.service.ts — add this helper function (exported)
export function normalizeBmcPersona(persona: string): CallerRole {
    const map: Record<string, CallerRole> = {
        AdditionalInsured: 'Additional Insured',
        AdditionalInterest: 'Additional Interest',
        MortgageServices: 'Mortgage Services',
        Insured: 'Insured',
        'Co-Applicant': 'Insured',
        Producer: 'Producer',
        Mortgage: 'Mortgage',
    };
    return map[persona] ?? 'Others';
}

@Component({
    selector: 'app-ivr-profile-card',

    imports: [FormsModule, MatCardModule, MatIconModule, ...PSIC_COMPONENTS],
    templateUrl: './ivr-profile-card.component.html',
    styleUrl: './ivr-profile-card.component.scss',
})
export class IvrProfileCardComponent {
    private callerService = inject(CallerService);
    private connectService = inject(ConnectService);
    private store = inject(Store);

    get phoneNumber(): string {
        const attrs = this.connectService.attributes || {};
        return attrs.callBackNumber || attrs.CustomerNumber || attrs.CustomerPhoneNumber || attrs.callerNumber || '';
    }

    callerOptions: CallerRole[] = [
        'Insured',
        'Additional Insured',
        'Additional Interest',
        'Producer',
        'Mortgage',
        'Mortgage Services',
        'Others',
    ];
    radioLeft = true;
    callerTypeField = new Field({
        name: 'callerType',
        value: '',
        caption: '',
        type: 'radio',
        required: false,
        description: '',
        options: {
            optionsList: this.callerOptions.map(
                (option) =>
                    new Field({
                        name: 'callerType',
                        value: option,
                        caption: '',
                        description: option,
                        type: 'radio',
                        required: false,
                        id: option.toLowerCase().replace(/\s+/g, '-'),
                    })
            ),
        },
    });

    selectedCaller = this.callerService.ivrSelectedRole;

    selectedProducerCode = signal('');

    selectedCallerValue = computed<CallerRole>(() => {
        const selected = this.selectedCaller();
        if (selected) return selected;
        return this.mapAttributesToCallerRole(this.connectService.attributes || {});
    });

    /** null = not yet validated, true = match, false = mismatch */
    profilesMatch = this.callerService.profilesMatch;

    // Replace profileMismatch computed with this
    profileMismatch = computed(() => {
        const contacts = this.callerService.bmcContacts();
        if (!contacts.length) return false;
        const ivrRole = this.selectedCaller();
        return !contacts.some((c) => normalizeBmcPersona(c.persona) === ivrRole);
    });

    // producerProfiles = computed(() => this.callerService.dbCallerProfile().filter((p) => p.role === 'Producer'));

    producerDetails = computed(() => {
        if (this.selectedCaller() !== 'Producer') return null;
        const contact = this.callerService.bmcContacts().find((c) => c.persona === 'Producer');
        if (!contact?.producer) return null;
        const p = contact.producer;
        return [
            { label: 'Producer Code', value: p.producercode ?? '—', isProducerCode: true, isInput: true },
            { label: 'Producer Name', value: p.producer_fullname ?? '—', isProducerCode: false, isInput: false },
            { label: 'Partner Code', value: p.partnercode ?? '—', isProducerCode: false, isInput: false },
            { label: 'Partner Name', value: p.partnername ?? '—', isProducerCode: false, isInput: false },
        ];
    });

    constructor(@Inject(APP_CONFIG) private config: AppConfig) {
        effect(() => {
            const role = this.mapAttributesToCallerRole(this.connectService.attributes || {});
            this.callerService.ivrSelectedRole.set(role);
            this.store.dispatch(setCallerRole({ role }));
        });

        // Sync producer code from BMC when Producer is selected.
        effect(() => {
            if (this.selectedCaller() === 'Producer') {
                const contact = this.callerService.bmcContacts().find((c) => c.persona === 'Producer');
                this.selectedProducerCode.set(contact?.producer?.producercode ?? '');
            } else {
                this.selectedProducerCode.set('');
            }
        });
    }

    navigateToProducer(producerCode: string) {
        window.open(`${this.config.amsPortalUrl.replace('<producerCode>', producerCode)}`, '_blank');
    }

    onSelect(option: CallerRole) {
        this.callerService.ivrSelectedRole.set(option);
        this.store.dispatch(setCallerRole({ role: option }));
    }

    private mapAttributesToCallerRole(attrs: any): CallerRole {
        return this.callerService.mapPersonaToRole(attrs.Persona || attrs.userType || attrs.UserType || '');
    }
}
